import React, { Children } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
export function PrivacyPolicy() {
  const navigate = useNavigate();
  return <div className="max-w-3xl mx-auto py-12 px-4 animate-in fade-in duration-500" data-id="element-268">
      <button onClick={() => navigate(-1)} className="flex items-center text-sm text-gray-500 hover:text-gray-900 mb-8 transition-colors" data-id="element-269">
        <ArrowLeft className="h-4 w-4 mr-2" data-id="element-270" />
        Back
      </button>

      <h1 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-271">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-10" data-id="element-272">Last updated: February 2026</p>

      <div className="prose prose-gray max-w-none space-y-8 text-gray-700 text-[15px] leading-relaxed" data-id="element-273">
        {/* 1. Introduction */}
        <section data-id="element-274">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-275">
            1. Introduction
          </h2>
          <p data-id="element-276">
            ProvenanceOne ("we", "us", "our"), the company behind MCPGuard, is
            committed to protecting your personal data and respecting your
            privacy. This Privacy Policy explains how we collect, use, store,
            and share your personal data when you visit our website, use our
            services, or interact with us.
          </p>
          <p data-id="element-277">
            We are a company registered in England and Wales. For the purposes
            of the UK General Data Protection Regulation (UK GDPR) and the Data
            Protection Act 2018, we are the data controller.
          </p>
        </section>

        {/* 2. Data Controller */}
        <section data-id="element-278">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-279">
            2. Data Controller
          </h2>
          <p data-id="element-280">The data controller responsible for your personal data is:</p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm space-y-1 mt-2" data-id="element-281">
            <p className="font-medium text-gray-900" data-id="element-282">ProvenanceOne</p>
            <p data-id="element-283">Email: privacy@provenanceone.ai</p>
          </div>
          <p className="mt-3" data-id="element-284">
            If you have any questions about this Privacy Policy or our data
            practices, please contact us using the details above.
          </p>
        </section>

        {/* 3. What Data We Collect */}
        <section data-id="element-285">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-286">
            3. What Data We Collect
          </h2>
          <p data-id="element-287">
            We may collect and process the following categories of personal
            data:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-288">
            <li data-id="element-289">
              <strong data-id="element-290">Identity data:</strong> your name, job title, and company
              name.
            </li>
            <li data-id="element-291">
              <strong data-id="element-292">Contact data:</strong> your email address and any other
              contact details you provide.
            </li>
            <li data-id="element-293">
              <strong data-id="element-294">Communications data:</strong> messages you send us through
              our contact form or by email.
            </li>
            <li data-id="element-295">
              <strong data-id="element-296">Technical data:</strong> IP address, browser type and
              version, time zone setting, operating system, and other technology
              identifiers on the device you use to access our website.
            </li>
            <li data-id="element-297">
              <strong data-id="element-298">Usage data:</strong> information about how you use our
              website, including pages visited, time spent, and navigation
              paths.
            </li>
          </ul>
          <p className="mt-3" data-id="element-299">
            We do not collect any special category data (such as data about your
            race, ethnicity, religious beliefs, health, or sexual orientation).
          </p>
        </section>

        {/* 4. How We Collect Your Data */}
        <section data-id="element-300">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-301">
            4. How We Collect Your Data
          </h2>
          <p data-id="element-302">We collect personal data through:</p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-303">
            <li data-id="element-304">
              <strong data-id="element-305">Direct interactions:</strong> when you fill in our early
              access or contact forms, or correspond with us by email.
            </li>
            <li data-id="element-306">
              <strong data-id="element-307">Automated technologies:</strong> as you navigate our
              website, we may automatically collect technical and usage data
              through cookies and similar technologies.
            </li>
          </ul>
        </section>

        {/* 5. Legal Basis for Processing */}
        <section data-id="element-308">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-309">
            5. Legal Basis for Processing
          </h2>
          <p data-id="element-310">
            Under the UK GDPR, we must have a lawful basis for processing your
            personal data. We rely on the following legal bases:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-311">
            <li data-id="element-312">
              <strong data-id="element-313">Consent (Article 6(1)(a)):</strong> where you have given
              clear consent for us to process your personal data for a specific
              purpose, such as joining our waiting list or receiving
              communications.
            </li>
            <li data-id="element-314">
              <strong data-id="element-315">Legitimate interests (Article 6(1)(f)):</strong> where
              processing is necessary for our legitimate interests (or those of
              a third party) and your interests and fundamental rights do not
              override those interests. This includes operating and improving
              our website, and understanding how our services are used.
            </li>
            <li data-id="element-316">
              <strong data-id="element-317">Contractual necessity (Article 6(1)(b)):</strong> where
              processing is necessary for the performance of a contract with you
              or to take steps at your request before entering into a contract.
            </li>
          </ul>
        </section>

        {/* 6. How We Use Your Data */}
        <section data-id="element-318">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-319">
            6. How We Use Your Data
          </h2>
          <p data-id="element-320">We use your personal data to:</p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-321">
            <li data-id="element-322">
              Process and manage your early access or waiting list registration.
            </li>
            <li data-id="element-323">Respond to your enquiries and provide support.</li>
            <li data-id="element-324">
              Send you product updates and communications you have opted into.
            </li>
            <li data-id="element-325">
              Analyse website usage to improve our services and user experience.
            </li>
            <li data-id="element-326">Comply with legal obligations.</li>
          </ul>
        </section>

        {/* 7. Data Sharing */}
        <section data-id="element-327">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-328">
            7. Data Sharing
          </h2>
          <p data-id="element-329">
            We do not sell your personal data. We may share your data with the
            following categories of recipients:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-330">
            <li data-id="element-331">
              <strong data-id="element-332">Service providers:</strong> third-party companies that
              provide services on our behalf, such as form processing (e.g.,
              Formspree), hosting, and analytics. These providers are
              contractually obligated to protect your data.
            </li>
            <li data-id="element-333">
              <strong data-id="element-334">Legal requirements:</strong> where we are required to
              disclose your data by law, regulation, or legal process.
            </li>
          </ul>
        </section>

        {/* 8. International Transfers */}
        <section data-id="element-335">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-336">
            8. International Transfers
          </h2>
          <p data-id="element-337">
            Some of our service providers may be based outside the United
            Kingdom. Where we transfer your personal data outside the UK, we
            ensure that appropriate safeguards are in place in accordance with
            UK GDPR requirements, such as standard contractual clauses approved
            by the Information Commissioner's Office (ICO) or transfers to
            countries with an adequacy decision.
          </p>
        </section>

        {/* 9. Data Retention */}
        <section data-id="element-338">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-339">
            9. Data Retention
          </h2>
          <p data-id="element-340">
            We retain your personal data only for as long as necessary to fulfil
            the purposes for which it was collected, including to satisfy any
            legal, accounting, or reporting requirements. When your data is no
            longer required, we will securely delete or anonymise it.
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-341">
            <li data-id="element-342">
              <strong data-id="element-343">Waiting list data:</strong> retained until you are granted
              access or you request removal.
            </li>
            <li data-id="element-344">
              <strong data-id="element-345">Contact form data:</strong> retained for up to 12 months
              after your last interaction.
            </li>
            <li data-id="element-346">
              <strong data-id="element-347">Technical and usage data:</strong> retained for up to 12
              months.
            </li>
          </ul>
        </section>

        {/* 10. Your Rights */}
        <section data-id="element-348">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-349">
            10. Your Rights
          </h2>
          <p data-id="element-350">
            Under the UK GDPR, you have the following rights in relation to your
            personal data:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-351">
            <li data-id="element-352">
              <strong data-id="element-353">Right of access:</strong> you can request a copy of the
              personal data we hold about you.
            </li>
            <li data-id="element-354">
              <strong data-id="element-355">Right to rectification:</strong> you can ask us to correct
              inaccurate or incomplete data.
            </li>
            <li data-id="element-356">
              <strong data-id="element-357">Right to erasure:</strong> you can ask us to delete your
              personal data in certain circumstances.
            </li>
            <li data-id="element-358">
              <strong data-id="element-359">Right to restrict processing:</strong> you can ask us to
              limit how we use your data.
            </li>
            <li data-id="element-360">
              <strong data-id="element-361">Right to data portability:</strong> you can request that
              we transfer your data to another organisation in a structured,
              commonly used format.
            </li>
            <li data-id="element-362">
              <strong data-id="element-363">Right to object:</strong> you can object to our processing
              of your data where we rely on legitimate interests.
            </li>
            <li data-id="element-364">
              <strong data-id="element-365">Right to withdraw consent:</strong> where we rely on
              consent, you can withdraw it at any time without affecting the
              lawfulness of processing carried out before withdrawal.
            </li>
          </ul>
          <p className="mt-3" data-id="element-366">
            To exercise any of these rights, please contact us at{' '}
            <strong data-id="element-367">privacy@provenanceone.ai</strong>. We will respond within
            one month of receiving your request.
          </p>
        </section>

        {/* 11. Cookies */}
        <section data-id="element-368">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-369">
            11. Cookies
          </h2>
          <p data-id="element-370">
            Our website may use cookies and similar technologies to distinguish
            you from other users and to improve your experience. Cookies are
            small text files placed on your device by your browser.
          </p>
          <p className="mt-2" data-id="element-371">We use the following types of cookies:</p>
          <ul className="list-disc pl-5 space-y-2 mt-3" data-id="element-372">
            <li data-id="element-373">
              <strong data-id="element-374">Strictly necessary cookies:</strong> required for the
              operation of our website.
            </li>
            <li data-id="element-375">
              <strong data-id="element-376">Analytical cookies:</strong> help us understand how
              visitors interact with our website so we can improve it.
            </li>
          </ul>
          <p className="mt-3" data-id="element-377">
            You can set your browser to refuse all or some cookies, or to alert
            you when websites set or access cookies. If you disable or refuse
            cookies, some parts of our website may become inaccessible or not
            function properly.
          </p>
          <p className="mt-2" data-id="element-378">
            For more information about cookies and your choices, visit{' '}
            <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" data-id="element-379">
              www.allaboutcookies.org
            </a>
            .
          </p>
        </section>

        {/* 12. Children */}
        <section data-id="element-380">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-381">
            12. Children's Data
          </h2>
          <p data-id="element-382">
            Our services are not directed at individuals under the age of 18. We
            do not knowingly collect personal data from children. If you believe
            we have collected data from a child, please contact us immediately
            and we will take steps to delete it.
          </p>
        </section>

        {/* 13. Changes */}
        <section data-id="element-383">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-384">
            13. Changes to This Policy
          </h2>
          <p data-id="element-385">
            We may update this Privacy Policy from time to time. Any changes
            will be posted on this page with an updated revision date. We
            encourage you to review this policy periodically.
          </p>
        </section>

        {/* 14. Complaints */}
        <section data-id="element-386">
          <h2 className="text-xl font-semibold text-gray-900 mb-3" data-id="element-387">
            14. Complaints
          </h2>
          <p data-id="element-388">
            If you are unhappy with how we have handled your personal data, you
            have the right to lodge a complaint with the Information
            Commissioner's Office (ICO), the UK's supervisory authority for data
            protection:
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm space-y-1 mt-2" data-id="element-389">
            <p className="font-medium text-gray-900" data-id="element-390">
              Information Commissioner's Office
            </p>
            <p data-id="element-391">
              Website:{' '}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" data-id="element-392">
                ico.org.uk
              </a>
            </p>
            <p data-id="element-393">Telephone: 0303 123 1113</p>
          </div>
          <p className="mt-3" data-id="element-394">
            We would appreciate the opportunity to address your concerns before
            you approach the ICO, so please contact us first at{' '}
            <strong data-id="element-395">privacy@provenanceone.ai</strong>.
          </p>
        </section>
      </div>
    </div>;
}